/** Common US workspace timezones shared by activation and CRM configuration. */

export const DEFAULT_WORKSPACE_TIME_ZONE = "America/New_York"

export const US_WORKSPACE_TIME_ZONES = [
  { label: "US Eastern", value: "America/New_York" },
  { label: "US Central", value: "America/Chicago" },
  { label: "US Mountain", value: "America/Denver" },
  { label: "US Pacific", value: "America/Los_Angeles" },
  { label: "Alaska", value: "America/Anchorage" },
  { label: "Hawaii", value: "Pacific/Honolulu" },
] as const

export type UsWorkspaceTimeZoneValue = (typeof US_WORKSPACE_TIME_ZONES)[number]["value"]

const ALLOWED = new Set<string>(US_WORKSPACE_TIME_ZONES.map((z) => z.value))

const IANA_TO_LABEL = Object.fromEntries(
  US_WORKSPACE_TIME_ZONES.map(({ label, value }) => [value, label]),
) as Record<string, string>

/** Legacy short labels still accepted when normalizing stored values. */
const LEGACY_LABEL_TO_VALUE: Record<string, string> = {
  Eastern: "America/New_York",
  Central: "America/Chicago",
  Mountain: "America/Denver",
  Pacific: "America/Los_Angeles",
  Alaska: "America/Anchorage",
  Hawaii: "Pacific/Honolulu",
}

export function detectBrowserWorkspaceTimeZone(): string {
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
    return DEFAULT_WORKSPACE_TIME_ZONE
  }
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (detected && ALLOWED.has(detected)) {
    return detected
  }
  return DEFAULT_WORKSPACE_TIME_ZONE
}

export function normalizeWorkspaceTimeZone(value: string | null | undefined): string {
  const raw = (value ?? "").trim()
  if (!raw) return DEFAULT_WORKSPACE_TIME_ZONE
  const byLabel = US_WORKSPACE_TIME_ZONES.find(
    (z) => z.label.toLowerCase() === raw.toLowerCase(),
  )
  if (byLabel) return byLabel.value
  const legacyValue = LEGACY_LABEL_TO_VALUE[raw]
  if (legacyValue) return legacyValue
  if (ALLOWED.has(raw)) return raw
  return DEFAULT_WORKSPACE_TIME_ZONE
}

export function workspaceTimeZoneLabel(value: string | null | undefined): string {
  const normalized = normalizeWorkspaceTimeZone(value)
  return IANA_TO_LABEL[normalized] ?? "US Eastern"
}

export function workspaceTimeZoneOptions(): { label: string; value: string }[] {
  return US_WORKSPACE_TIME_ZONES.map(({ label, value }) => ({ label, value }))
}
